import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Spinner } from 'ui';
import languageMapper from 'utils/languageMapper';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import {
  isAuthTokenValid,
  removeAuthTokenFromLocalStorage,
} from 'utils/centralAuthToken';
import supportsLocalStorage from 'utils/supportsLocalStorage';

const UCP = lazy(() =>
  import(/* webpackChunkName: "UCP" */ '@seismic/universal-content-picker'),
);

const Loader = () => {
  return (
    <div className="SeismicContent--Spinner">
      <Spinner size="larger" />
    </div>
  );
};

const SeismicContentPicker = ({ documentPicked, pickerSettings }) => {
  const [seismicToken, setSeismicToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => {
    request
      .get(routes.SEISMIC.AUTH_TOKEN)
      .then(({ token }) => {
        if (!isAuthTokenValid(token)) {
          throw new Error('Seismic auth token received is invalid');
        }

        if (supportsLocalStorage) {
          localStorage.setItem('seismicAuthToken', token);
        }
        setSeismicToken(token);
        setLoading(false);
        return token;
      })
      .fail(e => {
        const message =
          e.message ||
          'An error has occurred while trying to get Seismic auth token';
        console.error(message);
        removeAuthTokenFromLocalStorage();
        setLoading(false);
        return false;
      });
  };

  const onTokenExpired = () =>
    new Promise(resolve => {
      const token = getToken();
      if (token) {
        resolve({ isSuccess: true, token });
      } else {
        resolve({ isSuccess: false });
      }
    });

  useEffect(() => {
    const authToken =
      supportsLocalStorage && localStorage.getItem('seismicAuthToken');
    if (!isAuthTokenValid(authToken)) {
      getToken();
    } else {
      setSeismicToken(authToken);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="SeismicContentPicker">
      <Suspense fallback={<Loader />}>
        <UCP
          apiOptions={{
            authTokenResolver: () => seismicToken,
            onTokenExpired,
          }}
          settings={{
            repositories: ['doccenter'],
            selectionMode: 'single',
            allowFolderSelection: false,
            viewMode: 'wide',
            language: languageMapper(),
            application: 'Lessonly',
            showPreview: true,
            customizations: {
              preview: {
                displayPreviewOnContentIcon: true,
                displayPreviewOnPreviewButton: true,
                previewOnAction: 'click',
                triggerOnPreviewCallback: false,
                hideInbuiltPreview: false,
              },
            },
            ...pickerSettings,
          }}
          callbacks={{
            onSelectionChanged: items => {
              documentPicked(items);
            },
          }}
        />
      </Suspense>
    </div>
  );
};

export default SeismicContentPicker;
